import { differenceInWeeks, format } from "date-fns";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, IconButton, Typography } from "@/components/atoms";
import { useOperator, useProfile } from "@/hooks";
import { useLazyRequestVehicleDocumentQuery } from "@/redux/apis/document/documentApi";
import { downloadFile } from "@/utils";
import { getInsuranceStatus } from "../../MyVehicles/helpers";
import { VehicleDetailCard } from "../common/VehicleDetailCard";
import { useViewVehicleContext } from "../context";

export const VehicleInsuranceDetailCard = () => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const { dateFormat } = useProfile();
  const { vehicle } = useViewVehicleContext();
  const [requestDocument, { isLoading }] = useLazyRequestVehicleDocumentQuery();

  const status = getInsuranceStatus(vehicle);
  const insuranceDoc = vehicle.documents.find((doc) => doc.documentType === "INSURANCE");

  const isAffiliateVehicle = vehicle.owner.uuid !== operatorId;
  const isActionsDisabled = vehicle.metadata.isArchived || isAffiliateVehicle;

  const tooltip = useMemo(() => {
    if (!insuranceDoc || !insuranceDoc.expiryDate)
      return <Typography className="leading-tight">Missing vehicle insurance document.</Typography>;

    switch (status) {
      case "expiring": {
        const expiryDate = new Date(insuranceDoc.expiryDate);
        const difference = differenceInWeeks(expiryDate, new Date(), {
          roundingMethod: "ceil",
        });

        return (
          <Typography className="leading-tight">
            Vehicle insurance expiring in {difference} {difference === 1 ? "week" : "weeks"}. <br /> Kindly update the insurance
          </Typography>
        );
      }

      case "expired":
        return <Typography className="leading-tight">Vehicle insurance expired.</Typography>;

      case "missing-details":
      case "unknown":
        return <Typography className="leading-tight">Missing vehicle insurance document.</Typography>;

      case "verified":
      default:
        return undefined;
    }
  }, [insuranceDoc, status]);

  const handleDownload = () => {
    if (!insuranceDoc) return;

    requestDocument({
      id: insuranceDoc.uuid,
      fileType: "pdf", //todo: get file type from document
      vehicleId: vehicle.uuid,
    })
      .unwrap()
      .then((res) => {
        downloadFile(res.filename, res.uuid);
      });
  };

  const DownloadButton = () => {
    return (
      <IconButton
        disabled={isLoading}
        iconName="ImportCurve"
        className="!bg-transparent p-1.5 text-neutral-black"
        iconSize="lg"
        variant="tertiary"
        onClick={handleDownload}
      />
    );
  };

  const ActionButton = () => {
    if (isActionsDisabled) return null;

    switch (status) {
      case "expiring":
      case "expired":
      case "missing-details":
      case "unknown":
        return <IconButton onClick={() => navigate("./edit")} iconName="Refresh2" variant="primary" size="sm" className="relative z-10" />;
      case "verified":
        return <DownloadButton />;
      default:
        return null;
    }
  };

  const description = insuranceDoc?.expiryDate
    ? `${status === "expired" ? "Expired" : "Expires"} on: ${format(new Date(insuranceDoc.expiryDate), dateFormat)}`
    : "N/A";

  return (
    <VehicleDetailCard
      title={
        <>
          Vehicle Insurance
          <StatusBadge status={status} />
        </>
      }
      description={description}
      tooltip={tooltip}
      icon={<Icon name="file-pdf" isCustom size="xl" className="text-neutral-black" />}
      action={<ActionButton />}
      actionClick={handleDownload}
      status={status}
    />
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case "verified":
      return <Icon name="Verify" className="text-info" variant="Bold" size="md" />;
    case "expiring":
      return <Icon name="Danger" size="md" className="text-warning" variant="Bold" />;
    case "unknown":
    case "missing-details":
    case "expired":
      return <Icon name="InfoCircle" size="md" className=" text-danger" variant="Bold" />;
    default:
      return null;
  }
};
